import { useState, useEffect, useCallback } from 'react';
import { subscribeToUnreadGroupCount, resetUnreadCount } from '../services/groupService';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * Hook for managing group notifications and unread counts
 */
const useGroupNotifications = () => {
  const { uid, status } = useAuth();
  const [unread, setUnread] = useState({ uid: null, count: 0 });
  const [error, setError] = useState(null);
  const unreadGroupCount = unread.uid === uid ? unread.count : 0;

  // Subscribe to unread group count
  useEffect(() => {
    // Wait for auth to resolve before subscribing
    if (status === 'loading') {
      return;
    }

    // Signed out state: reset immediately
    if (!uid) {
      setUnread({ uid: null, count: 0 });
      setError(null);
      return;
    }

    setError(null);

    // Use the service to subscribe to unread counts
    return subscribeToUnreadGroupCount(
      uid,
      (count) => setUnread({ uid, count }),
      (err) => {
        console.error('Error in useGroupNotifications:', err);
        setError(err?.message || 'Failed to load unread counts');
        setUnread({ uid, count: 0 });
      }
    );
  }, [uid, status]);

  /**
   * Reset unread count for a group
   * @param {string} groupId - The group ID to reset
   * @returns {Promise<boolean>} Success status
   */
  const markGroupAsRead = useCallback(async (groupId) => {
    try {
      const success = await resetUnreadCount(groupId);
      return success;
    } catch (err) {
      console.error('Error resetting unread count:', err);
      setError(err?.message || 'Failed to mark as read');
      return false;
    }
  }, []);

  return {
    unreadGroupCount,
    error,
    markGroupAsRead
  };
};

export default useGroupNotifications; 
